import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, inject, input, output, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { ItemsDropdownComponent } from '@shared/components/dropdown/template/items-dropdown/items-dropdown.component';
import { isReferenceCellType } from '@shared/components/table/table-cell/utils';
import { TableColumn } from '@shared/components/table/table.model';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import {
  ColumnResizeEvent,
  ColumnResizerDirective
} from '@shared/directives/column-resizer.directive';
import { DropdownDirective } from '@shared/directives/dropdown.directive';
import { DropdownItem } from '@shared/models/dropdown-item.model';
import { TableCustomisationService } from '@shared/services/table-customisation/table-customisation.service';

export interface TableHeaderConfig<T> {
  errors?: boolean;
  rowNumbers?: boolean;
  columnTooltips?: boolean;
  columnColors?: boolean;
  columnActionSettings?: {
    sortable: boolean;
    filterable: boolean;
    groupable: boolean;
    movable: boolean;
    resizeable: boolean;
    hideable: boolean;
  };
  selection?: {
    enabled: boolean;
    multiple?: boolean;
    highlightSelected?: boolean;
    action?: (row: T) => void;
    mode?: 'checkbox' | 'click';
  };
}

@Component({
  selector: 'app-table-header',
  imports: [
    CdkDropList,
    CheckboxComponent,
    CdkDrag,
    ColumnResizerDirective,
    TooltipComponent,
    DropdownComponent,
    DropdownDirective,
    ItemsDropdownComponent
  ],
  templateUrl: './table-header.component.html'
})
export class TableHeaderComponent<T> {
  tableId = input.required<string>();
  config = input<TableHeaderConfig<T>>({});
  tableColumnStyles = input<Record<string, string>>({});
  columns = input<TableColumn<T>[]>([]);
  hasActions = input<boolean>(false);
  hiddenColumns = input<DropdownItem[]>([]);
  onSort = input<(column: TableColumn<T>, direction: 'asc' | 'desc') => void>(() => {});
  removeSort = input<(column: TableColumn<T>) => void>(() => {});
  toggleSelection = input<() => void>(() => {});
  isAllRowSelected = input<boolean>(false);
  isAnySelectedRow = input<boolean>(false);

  addFilterOn = output<keyof T>();
  removeFilterOn = output<keyof T>();

  protected isHiddenColumnsDropdownOpen = signal<boolean>(false);

  private readonly translate = inject(TranslateService);
  private readonly tableCustomisationService = inject(TableCustomisationService);

  protected toggleHiddenColumnsDropdown(): void {
    this.isHiddenColumnsDropdownOpen.update((v) => !v);
  }

  protected onCloseHiddenColumnsDropdown = (): void => {
    this.isHiddenColumnsDropdownOpen.set(false);
  };

  protected onDrop(event: CdkDragDrop<TableColumn<T>[]>) {
    const id = this.tableId();
    if (!id) return;

    this.tableCustomisationService.reorderColumns(
      id,
      event.previousIndex,
      event.currentIndex
    );
  }

  protected onColumnResize(event: ColumnResizeEvent): void {
    const id = this.tableId();
    if (!id) {
      console.warn('TableId is required for column resizing persistence');
      return;
    }

    this.tableCustomisationService.updateColumnWidth(id, event.columnKey, event.newWidth);
  }

  /** Column action */
  protected columnHasActions(): boolean {
    return !!this.config().columnActionSettings;
  }

  private isColumnSortable(column: TableColumn<T>): boolean {
    return !!(
      this.columnHasActions() &&
      this.config().columnActionSettings?.sortable &&
      column.config?.sortable
    );
  }

  private isColumnFilterable(column: TableColumn<T>): boolean {
    return !!(
      this.columnHasActions() &&
      this.config().columnActionSettings?.filterable &&
      column.config?.filterable
    );
  }

  private isColumnGroupable(column: TableColumn<T>): boolean {
    return !!(
      this.columnHasActions() &&
      this.config().columnActionSettings?.groupable &&
      column.config?.groupable
    );
  }

  protected isColumnMovable(column: TableColumn<T>): boolean {
    return !!(
      this.columnHasActions() &&
      this.config().columnActionSettings?.movable &&
      column.config?.orderable
    );
  }

  protected isColumnResizeable(column: TableColumn<T>): boolean {
    return !!(
      this.columnHasActions() &&
      this.config().columnActionSettings?.resizeable &&
      column.config?.resizeable
    );
  }

  private isColumnHideable(column: TableColumn<T>): boolean {
    return !!(
      this.columnHasActions() &&
      this.config().columnActionSettings?.hideable &&
      column.config?.hideable
    );
  }

  private isReferenceColumn(column: TableColumn<T>): boolean {
    return !!column.referenceEntityType && isReferenceCellType(column.type);
  }

  protected columnIsFiltered(column: TableColumn<T>): boolean {
    return column.filtered ?? false;
  }

  protected columnActions(column: TableColumn<T>): DropdownItem[] {
    if (!this.columnHasActions()) return [];

    const groups: DropdownItem[][] = [];
    const id = this.tableId();

    if (this.isColumnSortable(column)) {
      groups.push([
        {
          label: this.translate.instant('table.actions.sort_ascending'),
          iconClass: ICONS.sortAscending,
          action: () => this.onSort()(column, 'asc')
        },
        {
          label: this.translate.instant('table.actions.sort_descending'),
          iconClass: ICONS.sortDescending,
          action: () => this.onSort()(column, 'desc')
        }
      ]);
    }

    if (this.isColumnFilterable(column)) {
      groups.push([
        {
          label: this.translate.instant('table.actions.filter'),
          iconClass: ICONS.funnel,
          action: () => this.addFilterOn.emit(column.key)
        }
      ]);
    }

    if (this.isReferenceColumn(column)) {
      const showingName = column.displayMode === 'name';
      groups.push([
        {
          label: this.translate.instant(
            showingName ? 'table.actions.show_uids' : 'table.actions.show_names'
          ),
          iconClass: showingName ? ICONS.fingerprint : ICONS.tag,
          action: () => {
            if (id) {
              this.tableCustomisationService.updateColumnDisplayMode(
                id,
                String(column.key),
                showingName ? 'uid' : 'name'
              );
            }
          }
        }
      ]);
    }

    if (this.isColumnGroupable(column)) {
      groups.push([
        {
          label: this.translate.instant('table.actions.group_by'),
          iconClass: ICONS.rows,
          action: () => {
            console.log(`Group by action for column: ${String(column.key)}`);
          }
        }
      ]);
    }

    if (this.isColumnHideable(column)) {
      groups.push([
        {
          label: this.translate.instant('table.actions.hide_column'),
          iconClass: ICONS.eyeSlash,
          action: () => {
            if (id) {
              this.tableCustomisationService.toggleColumnVisibility(id, String(column.key));
            }
          }
        }
      ]);
    }

    if (this.isColumnMovable(column)) {
      groups.push([
        {
          label: this.translate.instant('table.actions.move_left'),
          iconClass: ICONS.arrowLeft,
          action: () => {
            if (id) {
              this.tableCustomisationService.moveColumnLeft(id, String(column.key));
            }
          }
        },
        {
          label: this.translate.instant('table.actions.move_right'),
          iconClass: ICONS.arrowRight,
          action: () => {
            if (id) {
              this.tableCustomisationService.moveColumnRight(id, String(column.key));
            }
          }
        }
      ]);
    }

    return this.joinWithSeparators(groups);
  }

  protected clearColumnFilter(column: TableColumn<T>) {
    this.removeFilterOn.emit(column.key);
  }

  private joinWithSeparators(groups: DropdownItem[][]): DropdownItem[] {
    const result: DropdownItem[] = [];
    for (const group of groups) {
      if (!group || group.length === 0) continue;
      if (result.length > 0) result.push({ isSeparator: true });
      result.push(...group);
    }
    return result;
  }

  protected readonly String = String;
  protected readonly ICONS = ICONS;
}
